import {
  type PropsWithChildren,
  type ReactElement,
  cloneElement,
  isValidElement,
  useCallback,
  useRef,
} from "react";

import { useInvoicePdfCache } from "@/providers/invoice-pdf-cache";

type Props = PropsWithChildren<{
  /**
   * The invoice ID to prefetch when hovered.
   * Can be a single ID or an array of IDs.
   */
  invoiceId: string | string[] | null | undefined;
  /**
   * Delay in milliseconds before triggering the prefetch.
   * Helps avoid unnecessary fetches on quick mouse movements.
   * @default 0
   */
  delay?: number;
  /**
   * Whether prefetching is disabled.
   * @default false
   */
  disabled?: boolean;
}>;

/**
 * Wraps children and prefetches invoice PDFs on mouse hover.
 * Uses the existing PDF cache provider so fetched PDFs are immediately
 * available when the user navigates to view them.
 *
 * @example
 * // Single invoice prefetch on table row hover
 * <PrefetchPdfOnHover invoiceId={invoice.id}>
 *   <TableRow>...</TableRow>
 * </PrefetchPdfOnHover>
 *
 * @example
 * // Prefetch next/previous invoices on navigation button hover
 * <PrefetchPdfOnHover invoiceId={nextInvoiceId}>
 *   <Button onClick={goNext}>Next</Button>
 * </PrefetchPdfOnHover>
 *
 * @example
 * // Prefetch multiple invoices at once
 * <PrefetchPdfOnHover invoiceId={[nextId, prevId]}>
 *   <NavigationControls />
 * </PrefetchPdfOnHover>
 */
export function PrefetchPdfOnHover({
  children,
  invoiceId,
  delay = 0,
  disabled = false,
}: Props) {
  const { fetchPdf, getPdfBlob } = useInvoicePdfCache();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasPrefetchedRef = useRef<Set<string>>(new Set());

  const prefetch = useCallback(
    (ids: string[]) => {
      for (const id of ids) {
        if (getPdfBlob(id) || hasPrefetchedRef.current.has(id)) {
          continue;
        }

        hasPrefetchedRef.current.add(id);

        // Fire and forget - we don't need to await or handle errors here
        // The cache provider handles errors internally
        fetchPdf(id).catch(() => {
          // Remove from prefetched set so it can be retried
          hasPrefetchedRef.current.delete(id);
        });
      }
    },
    [fetchPdf, getPdfBlob],
  );

  const handleMouseEnter = useCallback(
    (originalHandler?: (e: React.MouseEvent) => void) =>
      (e: React.MouseEvent) => {
        originalHandler?.(e);

        if (disabled || !invoiceId) return;

        const ids = Array.isArray(invoiceId) ? invoiceId : [invoiceId];
        const validIds = ids.filter(Boolean) as string[];

        if (validIds.length === 0) return;

        // Clear any existing timeout
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        if (delay > 0) {
          timeoutRef.current = setTimeout(() => {
            prefetch(validIds);
          }, delay);
        } else {
          prefetch(validIds);
        }
      },
    [disabled, invoiceId, delay, prefetch],
  );

  const handleMouseLeave = useCallback(
    (originalHandler?: (e: React.MouseEvent) => void) =>
      (e: React.MouseEvent) => {
        originalHandler?.(e);

        // Cancel pending prefetch if mouse leaves before delay
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
      },
    [],
  );

  if (isValidElement(children)) {
    const child = children as ReactElement<{
      onMouseEnter?: (e: React.MouseEvent) => void;
      onMouseLeave?: (e: React.MouseEvent) => void;
    }>;

    return cloneElement(child, {
      onMouseEnter: handleMouseEnter(child.props.onMouseEnter),
      onMouseLeave: handleMouseLeave(child.props.onMouseLeave),
    });
  }

  return (
    <span
      onMouseEnter={handleMouseEnter()}
      onMouseLeave={handleMouseLeave()}
      style={{ display: "contents" }}
    >
      {children}
    </span>
  );
}
